import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar, DollarSign, User, Eye } from "lucide-react";
import { cn } from "@/lib/utils";

interface Deal {
  id: string;
  title: string;
  value: number | null;
  expected_close_date: string | null;
  contact?: {
    name: string;
    organization: string | null;
  };
}

interface DealCardProps {
  deal: Deal;
  onClick?: () => void;
  isDragging?: boolean;
}

export function DealCard({ deal, onClick, isDragging }: DealCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging: isSortableDragging,
  } = useSortable({ id: deal.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleViewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.();
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        "p-2 cursor-grab active:cursor-grabbing transition-shadow",
        (isDragging || isSortableDragging) && "opacity-50"
      )}
    >
      <h4 className="font-medium text-sm mb-1.5 line-clamp-1">{deal.title}</h4>
      
      <div className="space-y-1 text-xs text-muted-foreground">
        {deal.value !== null && (
          <div className="flex items-center gap-1">
            <DollarSign className="h-3 w-3 flex-shrink-0" />
            <span>${deal.value.toLocaleString()}</span>
          </div>
        )}
        
        {deal.contact && (
          <div className="flex items-center gap-1">
            <User className="h-3 w-3 flex-shrink-0" />
            <div className="flex flex-col min-w-0">
              <span className="line-clamp-1">{deal.contact.name}</span>
              {deal.contact.organization && (
                <span className="text-[10px] line-clamp-1 opacity-75">{deal.contact.organization}</span>
              )}
            </div>
          </div>
        )}
        
        {deal.expected_close_date && (
          <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3 flex-shrink-0" />
            <span className="text-[10px]">{new Date(deal.expected_close_date).toLocaleDateString()}</span>
          </div>
        )}
      </div>

      <Button
        variant="outline"
        size="sm"
        className="w-full mt-2 cursor-pointer text-xs h-7"
        onClick={handleViewClick}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <Eye className="h-3 w-3 mr-1" />
        Details
      </Button>
    </Card>
  );
}
